/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useEffect } from "react";
import { AuthProps } from "aws-amplify-react/props";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import {
  UsersQuery,
  UsersData,
  UserInfo,
  InviteUserMutation,
  InviteUserInput,
  UserInput,
  UpdateUserMutation,
  saveContactData,
} from "../../../graphql/user";
import {
  Segment,
  Transition,
  Container,
  Table,
  Dimmer,
  Loader,
  Button,
  Modal,
  Grid,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "./../../Components.module.css";
import { ContactForm } from "../Profile";
import AddUserForm from "./AddUserForm";
import {
  authCheckCallApi,
  getUserDescription,
  parseGraphQlError,
  isMonitoringPlan,
} from "../../Authenticator/utils";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import userImage from "../../Icon/User.svg";
import ConfirmationDialog from "../../ConfirmationDialog";
import { Ico } from "../../Icon/Icon";
import {
  OrganizationData,
  OrganizationQuery,
} from "../../../graphql/organization";
import { useTranslation } from "react-i18next";

const Contacts: React.FC<AuthProps> = ({ authData }) => {
  const authDataPayload = authData.signInUserSession.idToken.payload;
  const [showContact, setShowContact] = useState<any>(null);
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [sorting, setSorting] = useState<any>({
    sortBy: "name",
    reverse: false,
  });
  const [isMonitoringEnabled, setIsMonitoringEnabled] = useState(false);
  const { t } = useTranslation("common");

  const [
    getUsers,
    {
      loading: usersLoading,
      data: usersData,
      refetch: refetchUsers,
      error: queryError,
    },
  ] = useLazyQuery<UsersData>(UsersQuery, {
    onError: (e) => {
      !message &&
        setMessage({
          title: t("contacts"),
          message: `${t("users_error")} ${parseGraphQlError(e)}`,
        });
    },
    onCompleted: (data: any) => {
      if (!message && (queryError || !data)) {
        setMessage({
          title: t("contacts"),
          message: t("contact_data_error"),
        });
      }
    },
  });

  const [inviteUser, { loading: inviteUserLoading }] = useMutation(
    InviteUserMutation,
    {
      onError: (e) => {
        const errorMessage = {
          title: t("add_user"),
          message: t("invite_error"),
        };
        if (e.message.includes("Already a member")) {
          errorMessage.message = t("user_exists");
        } else if (
          e.message.includes("blocked domain") ||
          e.message.includes("Not authorized")
        ) {
          errorMessage.message = t("bad_address");
        }
        setMessage(errorMessage);
      },
      onCompleted: () => {
        setShowContact(null);
        setMessage({
          title: t("add_user"),
          message: t("invite_success"),
        });
        refetchUsers();
      },
    }
  );

  const [getOrg] = useLazyQuery<OrganizationData>(OrganizationQuery, {
    fetchPolicy: "network-only",
    onCompleted: (data: any) => {
      if (
        data?.organization?.plan &&
        isMonitoringPlan(data.organization.plan) &&
        !isMonitoringEnabled
      ) {
        setIsMonitoringEnabled(true);
      }
    },
  });

  useEffect(() => {
    authCheckCallApi(getUsers);
    authCheckCallApi(getOrg);
  }, []);

  const getUserRow = (user: UserInfo) => {
    if (authData.attributes["custom:uid"] === user.id) {
      return null;
    }

    let rowClassName = styles.usersTableRow;
    if (user.disabled) {
      rowClassName = styles.usersTableRowDisabled;
    }

    return (
      <Table.Row
        key={user.id}
        onClick={() => setShowContact(user)}
        className={rowClassName}
      >
        <Table.Cell className={styles.userNameCol}>
          <div className={styles.imageContainer}>
            <img
              src={user.imageUrl || userImage}
              alt="User"
              className={styles.profileImage}
            />
          </div>
          {user.name}
        </Table.Cell>
        <Table.Cell>{user.email}</Table.Cell>
        <Table.Cell>
          {getUserDescription(user.role)}
          {user.status === "INVITED" && " (Invited)"}
        </Table.Cell>
      </Table.Row>
    );
  };

  const onAddUser = async (inviteUserInput: InviteUserInput) => {
    inviteUserInput.email = inviteUserInput.email.toLocaleLowerCase();
    inviteUserInput.orgId = authDataPayload.orgId;
    authCheckCallApi(inviteUser, {
      variables: { user: { ...inviteUserInput } },
    });
  };

  const [updateUser, { loading: updateUserLoading }] = useMutation(
    UpdateUserMutation,
    {
      onError: () => {
        setMessage({
          title: t("update_user"),
          message: t("update_user_error"),
        });
      },
      onCompleted: () => {
        if (showContact) {
          refetchUsers();
          setShowContact(null);
        }
      },
    }
  );

  const onSaveContact = async (updatedUser: saveContactData | null) => {
    if (!updatedUser) {
      setShowContact(false);
      refetchUsers();
      return;
    }

    const userInput: UserInput = {
      id: updatedUser.id,
      name: updatedUser.name,
      address: {
        line1: updatedUser.addressLine1,
        line2: updatedUser.addressLine2,
        city: updatedUser.addressCity,
        country: updatedUser.addressCountry,
        zip: updatedUser.addressZip,
        state: updatedUser.addressState,
        telephone1: updatedUser.addressTelephone1,
        telephone2: updatedUser.addressTelephone2,
      },
      organization: {
        jobTitle: updatedUser.jobTitle,
        role: updatedUser.role,
      },
    };

    authCheckCallApi(updateUser, { variables: { user: { ...userInput } } });
  };

  const showContactPopup = () => {
    return (
      <Transition transitionOnMount animation="fade" duration="250">
        <Modal
          closeOnDimmerClick={false}
          size="tiny"
          open
          onClose={() => setShowContact(false)}
        >
          {updateUserLoading && (
            <Dimmer active={true}>
              <Loader size="large" active={true}>
                {t("saving")}
              </Loader>
            </Dimmer>
          )}

          <Modal.Content>
            <div
              className={styles.sectionHeaderMargin}
              style={{ display: "flex" }}
            >
              {showContact === "new" ? "Add a new user" : "Edit User"}
              <div
                className={cStyles.closeButton}
                onClick={() => setShowContact(false)}
              >
                <Ico name="cancel" style={{ color: "#ffffff", margin: 0 }} />
              </div>
            </div>

            {showContact === "new" ? (
              <AddUserForm
                onAddUser={onAddUser}
                onCancel={() => setShowContact(false)}
                loading={inviteUserLoading}
                userRole={authDataPayload.role}
                message={t("send_invite")}
              />
            ) : (
              <ContactForm
                onFormSubmit={onSaveContact}
                onCancel={() => onSaveContact(null)}
                userId={showContact.id}
                currentUserRole={authDataPayload.role}
                readOnly={
                  authDataPayload.plan === "expired" ||
                  !authDataPayload.role.includes("ADMIN")
                }
                isMonitoringEnabled={isMonitoringEnabled}
              />
            )}
          </Modal.Content>
        </Modal>
      </Transition>
    );
  };

  const sortUsers = (valueA: UserInfo, valueB: UserInfo) => {
    let sortValueA, sortValueB;

    switch (sorting.sortBy) {
      case "role":
        sortValueA = getUserDescription(valueA.role);
        sortValueB = getUserDescription(valueB.role);
        break;

      case "email":
        sortValueA = valueA.email;
        sortValueB = valueB.email;
        break;

      default:
        sortValueA = valueA.name;
        sortValueB = valueB.name;
    }

    if (!sortValueA || !sortValueB) {
      return 0;
    }

    sortValueA = sortValueA.toLocaleLowerCase();
    sortValueB = sortValueB.toLocaleLowerCase();
    if (sortValueA < sortValueB) {
      return sorting.reverse ? 1 : -1;
    } else if (sortValueA > sortValueB) {
      return sorting.reverse ? -1 : 1;
    }
    return 0;
  };

  const setUserSorting = (sortBy: string) => {
    setSorting({
      sortBy: sortBy,
      reverse: sortBy === sorting.sortBy ? !sorting.reverse : false,
    });
  };

  const getSortIndicator = (columnName: string) => {
    if (sorting.sortBy === columnName) {
      return (
        <Ico
          name={sorting.reverse ? "down" : "up"}
          style={{ marginLeft: "8px", verticalAlign: "middle" }}
        />
      );
    }
    return null;
  };

  const getUsersList = () => {
    if (usersLoading) {
      return null;
    }

    if (usersData && usersData.users.length > 1) {
      const sortedUsers = usersData.users.sort(
        (userA: UserInfo, userB: UserInfo) => sortUsers(userA, userB)
      );

      return (
        <Table selectable sortable className={styles.usersTable} basic="very">
          <Table.Header>
            <Table.Row className={styles.invoiceTableHeader}>
              <Table.HeaderCell
                onClick={() => {
                  setUserSorting("name");
                }}
              >
                {t("name")}
                {getSortIndicator("name")}
              </Table.HeaderCell>
              <Table.HeaderCell
                onClick={() => {
                  setUserSorting("email");
                }}
              >
                {t("email")}
                {getSortIndicator("email")}
              </Table.HeaderCell>
              <Table.HeaderCell
                onClick={() => {
                  setUserSorting("role");
                }}
              >
                {t("role")}
                {getSortIndicator("role")}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{sortedUsers.map((user) => getUserRow(user))}</Table.Body>
        </Table>
      );
    }

    return (
      <Grid>
        <Grid.Column textAlign="center">
          <div className={styles.myAccountNoUsersLabel}>{t("no_users")}</div>
        </Grid.Column>
      </Grid>
    );
  };

  return (
    <React.Fragment>
      <Container id={styles.highlightsContainer}>
        <Transition transitionOnMount animation="fade in" duration="250">
          <Segment className={styles.myAccountSegment}>
            <Dimmer active={usersLoading}>
              <Loader size="large" active={usersLoading}>
                {t("loading")}
              </Loader>
            </Dimmer>

            <div className={styles.contactsHeaderContainer}>
              <div className={styles.sectionHeader}>{t("users")}</div>

              {authDataPayload.role.includes("ADMIN") && (
                <Button
                  primary
                  className={cStyles.buttonPrime}
                  onClick={() => setShowContact("new")}
                >
                  <Ico name="add_user" style={{ marginRight: "10px" }} />
                  {t("add_user")}
                </Button>
              )}
            </div>
            {getUsersList()}
          </Segment>
        </Transition>
      </Container>

      {showContact && showContactPopup()}

      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </React.Fragment>
  );
};

export default Contacts;
